import { cn } from '@chopchop-se/ui';
import type { ErpStatus } from '@warehouse/shared';
import { CloudOff, FlaskConical, ShieldAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * "Which Business Central am I looking at?" — answered before anything else on
 * every page in this area.
 *
 * The scaffold's note in `routes.tsx` is deliberate: this banner belongs to the
 * area, not the app shell, because the shell must not learn about BC. It is
 * also the one control that keeps an operator from pressing "Full omsynk"
 * against production while believing they are in the sandbox.
 */
export function EnvironmentBanner({ status }: { status: ErpStatus | undefined }): ReactNode {
  const { t } = useTranslation('bcIntegration');
  if (!status) return null;

  const variant =
    status.mode === 'null' ? 'disabled' : status.environmentKind === 'sandbox' ? 'sandbox' : 'production';

  const { icon, title, description, tone } = {
    sandbox: {
      icon: <FlaskConical className="size-5" aria-hidden />,
      title: t('banner.sandbox'),
      description: t('banner.sandboxDescription', { environment: status.environment ?? '' }),
      tone: 'border-amber-400/40 bg-amber-400/10 text-amber-900 dark:text-amber-100',
    },
    production: {
      icon: <ShieldAlert className="size-5" aria-hidden />,
      title: t('banner.production'),
      description: t('banner.productionDescription', { environment: status.environment ?? '' }),
      tone: 'border-red-500/40 bg-red-500/10 text-red-900 dark:text-red-100',
    },
    disabled: {
      icon: <CloudOff className="size-5" aria-hidden />,
      title: t('banner.disabled'),
      description: t('banner.disabledDescription'),
      tone: 'border-surface-border bg-surface-subtle text-ink-light',
    },
  }[variant];

  return (
    <div
      role="status"
      data-testid="erp-environment-banner"
      data-variant={variant}
      className={cn('mb-5 flex items-start gap-3 rounded-xl border px-4 py-3', tone)}
    >
      <span className="mt-0.5 shrink-0">{icon}</span>
      <div className="min-w-0">
        <p className="text-sm font-semibold">{title}</p>
        <p className="mt-0.5 text-sm opacity-90">{description}</p>
      </div>
    </div>
  );
}
